import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { TextArea } from '@progress/kendo-react-inputs';

import {
  useGetEventPlaylistChallenges,
  useGetPlaylistChallengeSubmissions
} from '../../../hooks/services/use-challenges';
import { formatSubDate, getStatusBadge } from '.';
import {
  EventPlaylistChallenge,
  EventPlaylistChallengeSubmissionListItem
} from '../../../types/event';

export const SubmissionList: React.FC<{
  notes?: string;
  playlistId: number | undefined;
  activityEventId: number | undefined;
  setSubmittingChallenge: React.Dispatch<
    React.SetStateAction<EventPlaylistChallenge | null>
  >;
  setSubmittedSubmission: React.Dispatch<
    React.SetStateAction<EventPlaylistChallengeSubmissionListItem | null>
  >;
}> = ({
  notes,
  activityEventId,
  playlistId,
  setSubmittingChallenge,
  setSubmittedSubmission
}) => {
  const { challenges, isLoadingChallenges } = useGetEventPlaylistChallenges(
    activityEventId,
    playlistId
  );

  const { submissions, isLoadingSubmissions } =
    useGetPlaylistChallengeSubmissions(activityEventId, playlistId);

  const isLoading = isLoadingChallenges || isLoadingSubmissions;

  return (
    <div className={'card p-4 gap-4'}>
      <div
        className="card p-4 border-1"
        style={{
          backgroundColor: '#F8FCFE'
        }}>
        <div className="mb-3 d-flex flex-column">
          <span
            style={{
              fontSize: '20px',
              fontWeight: 'bold'
            }}>
            Challenge submissions
          </span>
          <span
            style={{
              color: '#A5B7BF'
            }}>
            Submit your challenges for review and approval
          </span>
        </div>

        {isLoading ? (
          <div className="text-center py-3 text-muted">
            Loading challenges...
          </div>
        ) : challenges.length === 0 ? (
          <div className="text-center py-3 text-muted">
            No challenges assigned to this activity.
          </div>
        ) : (
          challenges.map((c, idx) => {
            const sub = submissions.find(
              (s) => s.challengeId === c.challengeId
            );
            const statusInfo = getStatusBadge(sub?.status);
            const isNotSubmitted =
              !sub || sub.status?.toLowerCase() === 'not submitted';

            return (
              <Row
                key={c.challengeId}
                className={`align-items-center py-2 m-0 ${
                  idx < challenges.length - 1 ? 'border-bottom' : ''
                }`}>
                <Col md={6} className={'pl-0'}>
                  <div className="d-flex flex-column mb-1">
                    <span className={'text-primary fw-bold'}>
                      {c.challengeName}
                    </span>
                    <span>{c.challengeDescription}</span>
                  </div>
                </Col>
                <Col md={2} className={'text-center'}>
                  {sub?.submittedDateUtc
                    ? formatSubDate(sub.submittedDateUtc)
                    : ''}
                </Col>
                <Col md={2} className={'text-center'}>
                  <span
                    style={{
                      padding: '4px 8px',
                      borderRadius: '5px',
                      fontSize: '14px',
                      ...statusInfo.style
                    }}>
                    {statusInfo.text}
                  </span>
                </Col>
                <Col md={2} className={'text-end pr-0'}>
                  {isNotSubmitted ? (
                    <Button
                      themeColor={'primary'}
                      className={'w-100'}
                      onClick={() => setSubmittingChallenge(c)}>
                      Submit attempt
                    </Button>
                  ) : (
                    <Button
                      themeColor={'secondary'}
                      fillMode={'outline'}
                      className={'w-100'}
                      onClick={() => {
                        setSubmittingChallenge(c);
                        setSubmittedSubmission(sub);
                      }}>
                      View submission
                    </Button>
                  )}
                </Col>
              </Row>
            );
          })
        )}
      </div>

      <div className={'d-flex flex-column'}>
        <Label>Activity notes</Label>
        <TextArea
          rows={3}
          value={notes}
          readOnly
          style={{ backgroundColor: '#e9ecef', color: '#6c757d', cursor: 'default' }}
        />
      </div>
    </div>
  );
};
